/**
 * PricingTabTotalsFooter
 *
 * Self-contained footer that fetches server-computed pricing totals via
 * useOfferPricingTotals and renders them inline below the pricing table.
 *
 * Key behaviors:
 * - Renders subtotalNet, discount (when >0), grandTotalNet from the server
 * - Money formatted via Intl.NumberFormat('pl-PL', { style: 'currency', currency })
 * - Totals reflect priced rows only (server-computed — unpriced rows are excluded)
 * - 422 mixed-currency: non-blocking warning banner instead of hard error
 * - Loading / no-data: renders nothing (invisible to the layout)
 * - All user-facing strings go through t()
 */
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { parseApiError } from '@ooh/contracts';
import { useOfferPricingTotals } from './api';
import { formatMoney } from '../units-tab/lib/money-format';

// ─── Helpers ─────────────────────────────────────────────────

function isMixedCurrencyError(error: unknown): boolean {
  const parsed = parseApiError(error);
  return parsed.statusCode === 422;
}

// ─── Props ────────────────────────────────────────────────────

export interface PricingTabTotalsFooterProps {
  offerId: number;
}

// ─── Component ───────────────────────────────────────────────

export function PricingTabTotalsFooter({ offerId }: PricingTabTotalsFooterProps) {
  const { t } = useTranslation('offers');
  const totalsQuery = useOfferPricingTotals(offerId);

  // Loading: render nothing — avoid layout shift
  if (totalsQuery.isLoading) {
    return null;
  }

  // Mixed-currency 422: non-blocking warning banner
  if (totalsQuery.isError && isMixedCurrencyError(totalsQuery.error)) {
    return (
      <div
        data-testid="pricing-totals-mixed-currency-warning"
        className="shrink-0 border-t border-border bg-amber-50 dark:bg-amber-950/20 px-6 py-2.5 text-[12px] text-amber-700 dark:text-amber-400 flex items-center gap-2"
        role="alert"
      >
        <span>{t('workspace.pricingTab.mixedCurrencyWarning')}</span>
      </div>
    );
  }

  // No data (other errors or undefined): render nothing
  if (!totalsQuery.data) {
    return null;
  }

  const {
    currency,
    subtotalNet,
    totalDiscountAmount,
    subtotalAfterDiscount,
    grandTotalNet,
    additionalServicesNet,
  } = totalsQuery.data;

  return (
    <div
      className="shrink-0 border-t border-border bg-background px-6 py-3 flex items-center gap-6 text-[12px]"
      data-testid="pricing-totals-footer"
    >
      <span className="text-muted-foreground" data-testid="pricing-totals-subtotal-net">
        {t('workspace.pricingTab.totals.subtotal')}:{' '}
        <span className="font-mono font-medium text-foreground">
          {formatMoney(subtotalNet, currency)}
        </span>
      </span>

      {totalDiscountAmount > 0 ? (
        <span className="text-muted-foreground" data-testid="pricing-totals-discount">
          {t('workspace.pricingTab.totals.discount')}:{' '}
          <span className="font-mono font-medium text-foreground">
            -{formatMoney(totalDiscountAmount, currency)}
          </span>
        </span>
      ) : null}

      {/* subtotalAfterDiscount — hidden visually but carries E2E testid for totals assertions */}
      <span
        className="sr-only"
        data-testid="pricing-totals-subtotal-after-discount"
        aria-hidden="true"
      >
        {formatMoney(subtotalAfterDiscount, currency)}
      </span>

      {additionalServicesNet > 0 ? (
        <span className="text-muted-foreground" data-testid="pricing-totals-additional-services">
          {t('workspace.pricingTab.totals.additionalServices')}:{' '}
          <span className="font-mono font-medium text-foreground">
            {formatMoney(additionalServicesNet, currency)}
          </span>
        </span>
      ) : null}

      <span className="ml-auto font-semibold">
        {t('workspace.pricingTab.totals.grandTotal')}:{' '}
        <span className="font-mono" data-testid="pricing-grand-total">
          {formatMoney(grandTotalNet, currency)}
        </span>
      </span>
    </div>
  );
}
